import {
    INVESTOR_INTRODUCTION,
    INVESTOR_AGEON_SCORE,
    INVESTOR_DIAGNOSTICS,
    INVESTOR_THERAPIES,
} from './investorContent';
/** Centered section header — matches Pillars / Journey / EBG. */
function SectionHeader({ chip, title, subtitle, accent }) {
    return (
        <div className="text-center max-w-2xl mx-auto mb-14 lg:mb-16" data-aos="fade-up">
            <span className="lp-chip mb-6">{chip}</span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-[2.65rem] text-ink-900 tracking-tight">
                {accent ? (
                    <>
                        {title.split(accent)[0]}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            {accent}
                        </span>
                        {title.split(accent)[1] || ''}
                    </>
                ) : title}
            </h2>
            {subtitle && (
                <>
                    <span aria-hidden className="mx-auto mt-5 block h-1 w-12 rounded-full bg-gradient-to-r from-brand-teal to-brand-gold" />
                    <p className="mt-5 text-ink-600 leading-relaxed text-base lg:text-lg">{subtitle}</p>
                </>
            )}
        </div>
    );
}

function PillarCard({ icon: Icon, title, desc, delay = 0 }) {
    return (
        <article
            className="lp-lift rounded-2xl bg-white p-6 ring-1 ring-ink-100 shadow-[0_8px_32px_-12px_rgba(15,17,21,0.08)]"
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-teal-50 text-brand-teal-700 ring-1 ring-brand-teal/10">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading font-bold text-lg text-ink-900">{title}</h3>
            {desc && <p className="mt-2 text-sm text-ink-600 leading-relaxed">{desc}</p>}
        </article>
    );
}

function DiagnosticRow({ index, icon: Icon, name, desc, delay = 0 }) {
    return (
        <div
            className="group landing-glass-light rounded-2xl p-6 flex items-start gap-5 transition hover:shadow-xl hover:shadow-brand-teal/5 hover:border-brand-teal/20"
            data-aos="fade-left"
            data-aos-delay={String(delay)}
        >
            <div className="text-xs font-mono text-brand-teal-700 mt-1 shrink-0 font-bold">
                {String(index + 1).padStart(2, '0')}
            </div>
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white grid place-items-center shrink-0 shadow-lg shadow-brand-teal/20">
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
                <div className="font-heading font-bold text-lg text-ink-900">{name}</div>
                <p className="text-sm text-ink-600 mt-1.5 leading-relaxed">{desc}</p>
            </div>
        </div>
    );
}

/* ── 1. Introduction (PDF p.2–7) ── */
function LandingIntroduction() {
    const s = INVESTOR_INTRODUCTION;
    return (
        <section id={s.id} className="relative scroll-mt-24 bg-white">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <SectionHeader
                    chip="AGEON Introduction"
                    title={s.title}
                    subtitle="India's longevity opportunity — from reactive care to measurable smart-ageing."
                />

                {/* Problem — hero statement */}
                <div className="max-w-4xl mx-auto text-center mb-16 lg:mb-20" data-aos="fade-up">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-brand-teal-700 mb-4">
                        {s.problem.eyebrow}
                    </p>
                    <h3 className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-ink-900 leading-tight tracking-tight">
                        {s.problem.heading}
                    </h3>
                    <p className="mt-6 text-ink-600 leading-relaxed text-base lg:text-lg max-w-3xl mx-auto">
                        {s.problem.body}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-2">
                        {s.problem.tags.map(tag => (
                            <span
                                key={tag}
                                className="inline-flex items-center rounded-full bg-ink-50 px-3.5 py-1.5 text-xs font-semibold text-ink-700 ring-1 ring-ink-100"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
}

/* ── 2. AGEON Score (PDF p.9–10) ── */
function LandingAgeonScore() {
    const s = INVESTOR_AGEON_SCORE;
    return (
        <section id={s.id} className="relative scroll-mt-24 bg-gradient-to-b from-ink-50 to-white border-y border-ink-100">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <SectionHeader
                    chip="Proprietary intelligence"
                    title="Introducing AGEON Score"
                    subtitle={s.intro.body}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
                    {s.pillars.map((p, i) => (
                        <PillarCard key={p.name} icon={p.icon} title={p.name} desc={p.desc} delay={i * 70} />
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ── 3. Diagnostics Engine (PDF p.11) — matches LandingDiagnostics layout ── */
function LandingDiagnosticsEngine() {
    const s = INVESTOR_DIAGNOSTICS;
    return (
        <section id={s.id} className="relative scroll-mt-24 bg-white">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-start">
                    <div className="lg:sticky lg:top-28" data-aos="fade-right">
                        <div className="landing-section-label mb-5">Diagnostics</div>
                        <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-ink-900">
                            {s.title}
                        </h2>
                        <p className="mt-5 text-ink-600 leading-relaxed text-lg">{s.intro.body}</p>
                    </div>
                    <div className="space-y-4">
                        {s.panels.map((p, i) => (
                            <DiagnosticRow
                                key={p.name}
                                index={i}
                                icon={p.icon}
                                name={p.name}
                                desc={p.desc}
                                delay={i * 70}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ── 4. Therapies to Protocol (PDF p.13) — pillar card grid ── */
function LandingTherapiesProtocol() {
    const s = INVESTOR_THERAPIES;
    return (
        <section id={s.id} className="relative scroll-mt-24 bg-ink-50 border-t border-ink-100">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <SectionHeader
                    chip="Signature programmes"
                    title="From therapies to protocols"
                    subtitle={s.intro.body}
                    accent="protocols"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {s.programs.map((p, i) => (
                        <PillarCard key={p.name} icon={p.icon} title={p.name} desc={p.desc} delay={i * 60} />
                    ))}
                </div>
            </div>
        </section>
    );
}

export default function LandingInvestorSections() {
    return (
        <>
            {/* <LandingIntroduction /> */}
            <LandingAgeonScore />
            <LandingDiagnosticsEngine />
            <LandingTherapiesProtocol />
        </>
    );
}
